import { useQuery, useMutation, useQueryClient } from "react-query";
import { useState, useEffect, useCallback } from "react";
import {
  getUserNotifications,
  getUnreadNotificationsCount,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  NotificationQueryParams,
  Notification
} from "../../services/api/notifications/notificationsApi";
import { useWebSocket } from "../websocket/useWebSocket";

/**
 * Hooks para gerenciar notificações
 * Seguindo padrão de hooks de API do projeto JBE Engenharia
 */

const notificationsApi = {
  fetchNotifications: async (params?: NotificationQueryParams): Promise<Notification[]> => {
    try {
      const { data } = await getUserNotifications(params);
      return data;
    } catch (error: any) {
      console.warn("Erro ao consultar notificações:", error.message);
      throw error;
    }
  },

  fetchUnreadCount: async (): Promise<number> => {
    try {
      const { data } = await getUnreadNotificationsCount();
      return data.count;
    } catch (error: any) {
      console.warn("Erro ao consultar contagem de notificações:", error.message);
      // Retornar 0 em caso de erro para evitar quebrar o componente
      return 0;
    }
  },

  markAsRead: async (notificationId: number) => {
    try {
      const { data } = await markNotificationAsRead(notificationId);
      return data;
    } catch (error: any) {
      console.error("Erro ao marcar notificação como lida:", error.message);
      throw error;
    }
  },

  markAsUnread: async (notificationId: number) => {
    try {
      const { data } = await markNotificationAsUnread(notificationId);
      return data;
    } catch (error: any) {
      console.error("Erro ao marcar notificação como não lida:", error.message);
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      const { data } = await markAllNotificationsAsRead();
      return data;
    } catch (error: any) {
      console.error("Erro ao marcar todas as notificações como lidas:", error.message);
      throw error;
    }
  },

};

// Hook para listar notificações com cache
export const useNotifications = (params?: NotificationQueryParams) => {
  return useQuery(
    ['notifications', params],
    () => notificationsApi.fetchNotifications(params),
    {
      staleTime: 1000 * 60 * 2, // 2 minutos
      cacheTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: true,
      enabled: true
    }
  );
};

// Hook para contagem de não lidas com polling automático
export const useUnreadNotificationsCount = (pollingInterval: number = 30000) => {
  return useQuery(
    ['notifications', 'unread-count'],
    notificationsApi.fetchUnreadCount,
    {
      staleTime: 1000 * 30, // 30 segundos
      cacheTime: 1000 * 60 * 2, // 2 minutos
      refetchInterval: pollingInterval, // Polling a cada 30 segundos
      refetchOnWindowFocus: true,
      enabled: true
    }
  );
};

// Hook para marcar notificação como lida
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationsApi.markAsRead, {
    onSuccess: () => {
      // Invalidar cache de notificações e contagem
      queryClient.invalidateQueries(['notifications']);
    },
    onError: (error) => {
      console.error("Erro ao marcar notificação como lida:", error);
    }
  });
};

// Hook para marcar notificação como não lida
export const useMarkNotificationAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationsApi.markAsUnread, {
    onSuccess: () => {
      // Invalidar cache de notificações e contagem
      queryClient.invalidateQueries(['notifications']);
    },
    onError: (error) => {
      console.error("Erro ao marcar notificação como não lida:", error);
    }
  });
};

// Hook para marcar todas as notificações como lidas
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationsApi.markAllAsRead, {
    onSuccess: () => {
      // Invalidar cache de notificações e contagem
      queryClient.invalidateQueries(['notifications']);
    },
    onError: (error) => {
      console.error("Erro ao marcar todas as notificações como lidas:", error);
    }
  });
};


// Hook customizado que combina todas as funcionalidades com WebSocket
export const useNotificationsManager = (token?: string) => {
  const queryClient = useQueryClient();
  const [realtimeUnreadCount, setRealtimeUnreadCount] = useState<number>(0);
  const [realtimeNotifications, setRealtimeNotifications] = useState<Notification[]>([]);

  // Hooks React Query padrão
  const unreadCountQuery = useUnreadNotificationsCount(0); // Desabilitar polling
  const notificationsQuery = useNotifications({ limit: 5 }); // 5 mais recentes para dropdown
  const markAsRead = useMarkNotificationAsRead();
  const markAsUnread = useMarkNotificationAsUnread();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  // WebSocket callbacks
  const handleNewNotification = useCallback((wsNotification: any) => {
    // Converter WebSocket notification para formato da API
    const apiNotification: Notification = {
      id: wsNotification.id,
      userId: 0, // Será preenchido pela API
      type: wsNotification.type as any,
      title: wsNotification.title,
      message: wsNotification.message,
      entityType: wsNotification.entityType || undefined,
      entityId: wsNotification.entityId || undefined,
      isRead: false,
      priority: wsNotification.priority as any,
      expiresAt: undefined,
      createdAt: wsNotification.createdAt,
      updatedAt: wsNotification.createdAt,
    };

    // Atualizar cache das notificações
    queryClient.setQueryData(['notifications', { limit: 5 }], (oldData: Notification[] | undefined) => {
      const newData = [apiNotification, ...(oldData || [])].slice(0, 5);
      setRealtimeNotifications(newData);
      return newData;
    });

    // Incrementar contagem de não lidas
    setRealtimeUnreadCount(prev => prev + 1);
    
    // Invalidar queries para manter sincronização
    queryClient.invalidateQueries(['notifications']);
  }, [queryClient]);

  const handleUnreadCountUpdate = useCallback((data: { count: number }) => {
    // Validar se count é um número válido
    if (typeof data.count === 'number' && data.count >= 0) {
      setRealtimeUnreadCount(data.count);
      
      // Atualizar cache da contagem
      queryClient.setQueryData(['notifications', 'unread-count'], data.count);
    } else {
      console.warn('Contagem de notificações inválida recebida via WebSocket:', data);
    }
  }, [queryClient]);

  const handleConnect = useCallback(() => {
    console.log('WebSocket conectado - recarregando notificações');
    // Recarregar dados quando conectar
    queryClient.invalidateQueries(['notifications']);
  }, [queryClient]);

  // Hook do WebSocket
  const webSocket = useWebSocket({
    onNewNotification: handleNewNotification,
    onUnreadCountUpdate: handleUnreadCountUpdate,
    onConnect: handleConnect,
    autoConnect: !!token,
    token: token,
  });

  // Sincronizar estados quando dados da API chegarem
  useEffect(() => {
    if (unreadCountQuery.data !== undefined && typeof unreadCountQuery.data === 'number') {
      setRealtimeUnreadCount(unreadCountQuery.data);
    }
  }, [unreadCountQuery.data]);

  useEffect(() => {
    if (notificationsQuery.data) {
      setRealtimeNotifications(notificationsQuery.data);
    }
  }, [notificationsQuery.data]);

  // Funções customizadas que atualizam estado realtime
  const customMarkAsRead = useCallback((notificationId: number) => {
    markAsRead.mutate(notificationId, {
      onSuccess: () => {
        // Atualizar estado realtime imediatamente
        setRealtimeUnreadCount(prev => Math.max(0, prev - 1));
        setRealtimeNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, isRead: true }
              : notification
          )
        );
        
        // WebSocket também pode notificar
        webSocket.markNotificationAsRead(notificationId);
      }
    });
  }, [markAsRead, webSocket]);

  const customMarkAsUnread = useCallback((notificationId: number) => {
    markAsUnread.mutate(notificationId, {
      onSuccess: () => {
        // Atualizar estado realtime imediatamente
        setRealtimeUnreadCount(prev => prev + 1);
        setRealtimeNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, isRead: false }
              : notification
          )
        );
      }
    });
  }, [markAsUnread]);

  const customMarkAllAsRead = useCallback(() => {
    markAllAsRead.mutate(undefined, {
      onSuccess: () => {
        // Atualizar estado realtime imediatamente
        setRealtimeUnreadCount(0);
        setRealtimeNotifications(prev => 
          prev.map(notification => ({ ...notification, isRead: true }))
        );
      }
    });
  }, [markAllAsRead]);


  return {
    // Estados combinados (realtime + API)
    unreadCount: realtimeUnreadCount,
    notifications: realtimeNotifications,
    isLoading: notificationsQuery.isLoading || unreadCountQuery.isLoading,
    error: notificationsQuery.error || unreadCountQuery.error,
    
    // WebSocket status
    isWebSocketConnected: webSocket.isConnected,
    reconnectAttempts: webSocket.reconnectAttempts,
    
    // Ações
    markAsRead: customMarkAsRead,
    markAsUnread: customMarkAsUnread,
    markAllAsRead: customMarkAllAsRead,
    connectWebSocket: webSocket.connect,
    disconnectWebSocket: webSocket.disconnect,
    
    // Refetch manual
    refetch: () => {
      notificationsQuery.refetch();
      unreadCountQuery.refetch();
      webSocket.requestUnreadCount();
    }
  };
};
